import forge from 'apis/forge';
import { Point, Rect } from 'dragonmouse-shared/geometry';
import { Frame, PartialFrame } from 'dragonmouse-shared/interfaces';
import { MenuItemAction } from 'menu/MenuItem';
import { selectActiveScene, selectUser } from 'redux/db/reducers';
import { canEdit, Direction, getFrame, getFrames } from 'redux/db/util';
import {
	activateLastTool,
	setActiveTool,
	setPanelsVisible,
	setSettingsModalVisible,
	setToolModified,
	showAlertModal,
	showConfirmModal,
	setGuestSignupModalVisible,
	showPromptModal,
	setActiveSceneId,
} from 'redux/mode/actions';
import { trackEconomyAction } from 'analytics';
import {
	AlertModalOptions,
	ConfirmModalOptions,
	PromptModalOptions,
	Store,
	ToolType,
	ZoomType,
	selectTransform,
} from 'redux/mode/reducers';
import store from 'redux/store';
import { TargetCanvas } from 'transfer/Target';
import { hasPrimaryModifier, hasSecondaryModifier, navigate } from 'utils';
import { Pick } from './classes';
import { executeCommand } from './commandManager';
import { MemorializeFrameUpdateCommand } from './commands';
import { profile } from 'routes';

export const getSceneRect = (canvas: Rect): Rect => {
	const topleft: Point = getScenePoint({ x: canvas.x, y: canvas.y });
	const bottomright: Point = getScenePoint({
		x: canvas.x + canvas.width,
		y: canvas.y + canvas.height,
	});

	return {
		x: topleft.x,
		y: topleft.y,
		width: bottomright.x - topleft.x,
		height: bottomright.y - topleft.y,
	};
};

export const getCanvasRect = (scene: Rect): Rect => {
	const topleft: Point = getCanvasPoint({ x: scene.x, y: scene.y });
	const bottomright: Point = getCanvasPoint({
		x: scene.x + scene.width,
		y: scene.y + scene.height,
	});

	return {
		x: topleft.x,
		y: topleft.y,
		width: bottomright.x - topleft.x,
		height: bottomright.y - topleft.y,
	};
};

export const getScenePoint = (canvas: Point): Point => {
	const transform = selectTransform(store.getState().mode);
	return {
		x: (canvas.x - transform.offset.x) / transform.scale,
		y: (canvas.y - transform.offset.y) / transform.scale,
	};
};

export const getCanvasPoint = (scene: Point): Point => {
	const transform = selectTransform(store.getState().mode);
	return {
		x: transform.scale * scene.x + transform.offset.x,
		y: transform.scale * scene.y + transform.offset.y,
	};
};

export const getCanvasPointForEvent = (e: any): Point => {
	const rect = e.target.getBoundingClientRect();
	return {
		x: e.clientX - rect.left,
		y: e.clientY - rect.top,
	};
};

export const getVisibleCanvasCenterInSceneCoordinates = (): Point => {
	const state = store.getState().mode;

	// Get canvas dimensions
	const canvasWidth = state.canvasWidth || 0;
	const canvasHeight = state.canvasHeight || 0;

	// Calculate the center of the canvas in canvas coordinates
	const canvasCenter: Point = {
		x: canvasWidth / 2,
		y: canvasHeight / 2,
	};

	// Convert the canvas center to scene coordinates
	return getScenePoint(canvasCenter);
};

export const getScenePointForEvent = (e: any): Point => {
	const canvas = getCanvasPointForEvent(e);
	return getScenePoint(canvas);
};

// Listen and respond to user keyboard events
let registeredEventListeners = false;
export function startListeningForEvents() {
	if (!registeredEventListeners) {
		window.addEventListener('keydown', globalKeydownHandler);
		window.addEventListener('keyup', globalKeyupHandler);

		window.addEventListener('gesturestart', (e) => e.preventDefault());
		window.addEventListener('gesturechange', (e) => e.preventDefault());
		window.addEventListener('gestureend', (e) => e.preventDefault());

		registeredEventListeners = true;
	}
}

export const displayPromptModal = (options: PromptModalOptions) => {
	store.dispatch(showPromptModal(options));
};

export const displayConfirmModal = (options: ConfirmModalOptions) => {
	store.dispatch(showConfirmModal(options));
};

export const displayAlertModal = (options: AlertModalOptions) => {
	store.dispatch(showAlertModal(options));
};

export const displayGuestSignupModal = () => {
	store.dispatch(setGuestSignupModalVisible(true));
};

export const displayViewOnlyModal = () => {
	const user = selectUser(store.getState().db);
	if (user?.id === 'guest') {
		displayGuestSignupModal();
		return;
	}

	displayAlertModal({
		title: "You're in view-only mode",
		description:
			"You can look around, but you'll need edit access to make changes. Ask the owner for edit access to start designing.",
		submitLabel: 'OK',
		onSubmit: () => {},
	});
};

export const displayUpgradeModal = (featureName: string) => {
	trackEconomyAction('feature_upgrade_modal_shown');

	displayConfirmModal({
		title: 'Upgrade Required',
		description: `${featureName} is available for Pro and Max subscribers. Upgrade to unlock this feature and more.`,
		cancelLabel: 'Close',
		submitLabel: 'Upgrade',
		onSubmit: () => {
			trackEconomyAction('upgrade_clicked');
			// Clear activeSceneId before navigating to prevent redirect loops
			store.dispatch(setActiveSceneId(null));
			navigate(profile());
		},
	});
};

export const MAX_SCALE = 6;
export const MIN_SCALE = 0.02;

export const updateScale = (state: Store, zoomType: ZoomType): number => {
	const transform = selectTransform(state);

	switch (zoomType) {
		case ZoomType.ZOOM_IN:
			return Math.min(transform.scale * 2, MAX_SCALE);
		case ZoomType.ZOOM_OUT:
			return Math.max(transform.scale / 2, MIN_SCALE);
		default:
			return transform.scale;
	}
};

export type FrameSide = 'left' | 'right' | 'top' | 'bottom';

export const alignSelectedFrames = (side: FrameSide) => {
	const { selected } = store.getState().mode;
	const selectedFrames: Frame[] = getFrames(selected.map((pick) => pick.frameId || ''));

	if (selectedFrames.length < 1) return;

	// Determine the alignment value.
	let alignValue = side === 'left' || side === 'top' ? Infinity : -Infinity;
	selectedFrames.forEach((f) => {
		switch (side) {
			case 'left':
				alignValue = Math.min(alignValue, f.x);
				break;
			case 'right':
				alignValue = Math.max(alignValue, f.x + f.width);
				break;
			case 'top':
				alignValue = Math.min(alignValue, f.y);
				break;
			case 'bottom':
				alignValue = Math.max(alignValue, f.y + f.height);
				break;
		}
		return true;
	});

	const before: PartialFrame[] = [];
	const after: PartialFrame[] = [];
	selectedFrames.forEach((f) => {
		const update: PartialFrame = { id: f.id };
		switch (side) {
			case 'left':
				before.push({ id: f.id, x: f.x });
				after.push({ id: f.id, x: alignValue });
				update.x = alignValue;
				break;
			case 'right':
				before.push({ id: f.id, x: f.x });
				after.push({ id: f.id, x: alignValue - f.width });
				update.x = alignValue - f.width;
				break;
			case 'top':
				before.push({ id: f.id, y: f.y });
				after.push({ id: f.id, y: alignValue });
				update.y = alignValue;
				break;
			case 'bottom':
				before.push({ id: f.id, y: f.y });
				after.push({ id: f.id, y: alignValue - f.height });
				update.y = alignValue - f.height;
				break;
		}
		forge.updateFrame({ update });
		executeCommand(new MemorializeFrameUpdateCommand(before, after));
	});
};

// move all selected frames by delta
// has no side effect if nothing is selected
export const moveSelectedFrames = (delta: Point) => {
	const { selected } = store.getState().mode;
	const selectedFrames: Frame[] = getFrames(selected.map((pick) => pick.frameId || ''));

	selectedFrames.forEach((f) => {
		const before: PartialFrame = { id: f.id, x: f.x, y: f.y };
		const after: PartialFrame = { id: f.id, x: f.x + delta.x, y: f.y + delta.y };
		forge.updateFrame({ update: after });
		executeCommand(new MemorializeFrameUpdateCommand([before], [after]));
	});
};

//------------------------------------------------------------------------------------------------
// INTERNAL FUNCTIONALITY
// not exported
//------------------------------------------------------------------------------------------------

const globalKeydownHandler = async (event: KeyboardEvent) => {
	const { panelsVisible, selected, canvasMousePosition } = store.getState().mode;
	const selectedFrameId = getSelectedFrameId(selected);
	const scene = selectActiveScene(store.getState().db, store.getState().mode.activeSceneId);
	const elementFocus = getElementFocusState();

	// Handle zoom shortcuts regardless of field focus
	switch (event.key) {
		case '-':
		case '_':
			if (event.metaKey || event.ctrlKey) {
				event.preventDefault();
				MenuItemAction.createZoomOut().callback();
				return;
			}
			break;
		case '=':
		case '+':
			if (event.metaKey || event.ctrlKey) {
				event.preventDefault();
				MenuItemAction.createZoomIn().callback();
				return;
			}
			break;
		case '0':
			if (hasPrimaryModifier(event)) {
				MenuItemAction.createZoomReset().callback();
				return;
			}
			break;
	}

	// Prevent any further keyboard shortcuts if a field is focused.
	if (elementFocus == ElementFocusState.FIELD) return;

	// Handle undo related shortcuts here
	switch (event.key) {
		case 'z':
		case 'Z':
			if (hasPrimaryModifier(event)) {
				event.preventDefault();
				if (event.shiftKey) {
					MenuItemAction.createRedo().callback();
				} else {
					MenuItemAction.createUndo().callback();
				}
			} else store.dispatch(setActiveTool(ToolType.ZOOM));
			break;
	}

	// Now we prevent any further shortcuts because a field is focused.
	// The remaining shortcuts are not undo related.
	if (elementFocus == ElementFocusState.UNDO_EXEMPT_FIELD) return;

	// Shortcuts that don't use modifiers and need to be highly available belong here. A <button> or
	// <select> for example might still be focused right now.
	// Note: For now, only Esc is in the category, we might want to move things like zooming in/out
	// and hiding/showing panels here. -dew
	switch (event.key) {
		case 'Escape':
			if (store.getState().mode.activeTool !== ToolType.SELECT) {
				store.dispatch(setActiveTool(ToolType.SELECT));
			}
			if (store.getState().mode.guestSignupModalVisible) {
				store.dispatch(setGuestSignupModalVisible(false));
			}
			break;
	}

	// Prevent any fruther shortcuts due to non-field element having focus.
	if (elementFocus == ElementFocusState.NON_FIELD) return;

	// things get weird quickly if we, for example, change tools when an active tool has its modal open
	if (isAnyModalVisible()) return;

	// Here belong the shortcuts that use modifiers, or that we do not want them to function when some
	// other element has focus.
	switch (event.key) {
		case '.':
		case '\\':
			if (hasPrimaryModifier(event)) {
				store.dispatch(setPanelsVisible(!panelsVisible));
			}
			break;
		case ',':
			if (hasPrimaryModifier(event)) {
				// show settings modal
				event.preventDefault();
				store.dispatch(setSettingsModalVisible(true));
			}
			break;
		case ' ':
			store.dispatch(setActiveTool(ToolType.HAND));
			break;
		case '@':
			MenuItemAction.createAddFramesToChat(getFrameIds(selected)).callback();
			break;
		case '!':
			event.preventDefault();
			MenuItemAction.createZoomToFitCanvas().callback();
			break;
		case '#':
			event.preventDefault();
			MenuItemAction.createZoomToFitSelection().callback();
			break;
		case 'a':
		case 'A':
		case 'å': // Option + a makes å on Mac
			if (!canEdit(scene)) {
				displayViewOnlyModal();
				return;
			}
			if (hasSecondaryModifier(event) || event.key === 'å') {
				// // find smallest x coordinate of selected frames
				// const selectedFrames: Frame[] = getFrames(selected.map((pick) => pick.frameId || ''));
				// const smallestX = Math.min(...selectedFrames.map((frame) => frame.x));
				// // shift all selected frames to the left by the smallest x coordinate
				// selectedFrames.forEach((frame) => {
				// 	store.dispatch(updateFrame(frame.frameId, { x: smallestX }));
				// });
				alignSelectedFrames('left');
				break;
			}
			break;
		case 'c':
		case 'C':
			if (hasPrimaryModifier(event)) {
				const selection = window.getSelection();
				if (selection?.toString()) {
					// copy the old-fashioned way
					navigator.clipboard.writeText(selection.toString());
				} else {
					// copy what's selected on the canvas
					const selectedFrameIds = getSelectedFrameIds(selected);

					if (selectedFrameIds.length > 0) {
						if (selectedFrameIds.length === 1) {
							const selectedFrame = getFrame(selectedFrameIds[0]);
							if (selectedFrame) {
								MenuItemAction.createCopyFrameUrl(selectedFrame).callback();
							}
						} else {
							const selectedFrames = selectedFrameIds
								.map((id) => getFrame(id))
								.filter((f): f is Frame => f !== null);
							if (selectedFrames.length > 0) {
								MenuItemAction.createCopyFrameUrl(selectedFrames).callback();
							}
						}
					} else {
					}
				}
				break;
			}
			break;
		case 'd':
		case 'D':
		case '∂': // Option + d makes ∂ on Mac
			if (!canEdit(scene)) {
				displayViewOnlyModal();
				return;
			}
			if (hasPrimaryModifier(event)) {
				// duplicate
			}
			if (hasSecondaryModifier(event) || event.key === '∂') {
				alignSelectedFrames('right');
			}
			break;
		// historically, the E and P keys triggered edit (which is now prototype mode). leaving it enabled for now for backwards compatibility
		case 'p':
		case 'P':
		case 'e':
		case 'E':
			event.preventDefault();
			const frameIdsP = getFrameIds(selected);
			if (frameIdsP.length === 1) {
				const preview = MenuItemAction.createOpenPrototype(frameIdsP[0]);
				if (preview && !preview.disabled) {
					preview.callback();
				}
			}
			break;
		case 's':
		case 'S':
		case 'ß': // Option + s makes ß on Mac
			if (!canEdit(scene)) {
				displayViewOnlyModal();
				return;
			}
			if (hasSecondaryModifier(event) || event.key === 'ß') {
				alignSelectedFrames('bottom');
				break;
			}
			break;
		case 'v':
		case 'V':
		case '◊': // Command + Option + Shift v makes ◊ on Mac
			if (!canEdit(scene)) {
				displayViewOnlyModal();
				return;
			}
			if (hasPrimaryModifier(event) || event.key === '◊') {
				const paste = await MenuItemAction.createPaste(
					new TargetCanvas(getScenePoint(canvasMousePosition))
				);
				if (paste) paste.callback();
			}
			break;
		case 'w':
		case 'W':
		case '∑': // Option + w makes ∑ on Mac
			if (!canEdit(scene)) {
				displayViewOnlyModal();
				return;
			}
			if (hasSecondaryModifier(event) || event.key === '∑') {
				alignSelectedFrames('top');
			}
			break;
		case 'x':
		case 'X':
			if (hasPrimaryModifier(event)) {
				// cut
			}
			break;
		case 'Alt':
			store.dispatch(setToolModified(true));
			break;
		case 'ArrowRight':
			event.preventDefault();
			if (!canEdit(scene)) {
				displayViewOnlyModal();
				return;
			}
			if (hasSecondaryModifier(event)) {
				moveSelectedFrames({ x: 1, y: 0 });
			} else if (event.shiftKey) {
				moveSelectedFrames({ x: 10, y: 0 });
			} else if (selectedFrameId) {
				MenuItemAction.createNavFromFrame(selectedFrameId, Direction.RIGHT).callback();
			}
			break;
		case 'ArrowLeft':
			event.preventDefault();
			if (!canEdit(scene)) {
				displayViewOnlyModal();
				return;
			}
			if (hasSecondaryModifier(event)) {
				moveSelectedFrames({ x: -1, y: 0 });
			} else if (event.shiftKey) {
				moveSelectedFrames({ x: -10, y: 0 });
			} else if (selectedFrameId) {
				MenuItemAction.createNavFromFrame(selectedFrameId, Direction.LEFT).callback();
			}
			break;
		case 'ArrowDown':
			event.preventDefault();
			if (!canEdit(scene)) {
				displayViewOnlyModal();
				return;
			}
			if (hasSecondaryModifier(event)) {
				moveSelectedFrames({ x: 0, y: 1 });
			} else if (event.shiftKey) {
				moveSelectedFrames({ x: 0, y: 10 });
			} else if (selectedFrameId) {
				MenuItemAction.createNavFromFrame(selectedFrameId, Direction.DOWN).callback();
			}
			break;
		case 'ArrowUp':
			event.preventDefault();
			if (!canEdit(scene)) {
				displayViewOnlyModal();
				return;
			}
			if (hasSecondaryModifier(event)) {
				moveSelectedFrames({ x: 0, y: -1 });
			} else if (event.shiftKey) {
				moveSelectedFrames({ x: 0, y: -10 });
			} else if (selectedFrameId) {
				MenuItemAction.createNavFromFrame(selectedFrameId, Direction.UP).callback();
			}
			break;
		case 'Backspace':
		case 'Delete':
			const frameIds = selected.filter((pick) => pick.isFrame()).map((pick) => pick.frameId);
			if (frameIds.length > 0) {
				MenuItemAction.createDeleteFrames(frameIds).callback();
			}

			/*
			// Delete selected frame elements
			let frameId: string | null = null;
			const elementIds = selected
				.filter((pick) => {
					const isElement = pick.isFrameElement();
					// We need the parent frame id to delete elements
					if (isElement) {
						if (frameId === null) {
							frameId = pick.frameId;
						}
					}
					return isElement;
				})
				.map((pick) => pick.elementId);
			if (elementIds.length > 0) {
				MenuItemAction.createDeleteFrameElements(frameId!, elementIds).callback();
			}
			*/
			break;
	}
};

const globalKeyupHandler = (event: KeyboardEvent) => {
	// don't do anything if the keyboard is being used for something else
	const elementFocus = getElementFocusState();
	if (elementFocus !== ElementFocusState.OTHER) return;

	// make these tools deactivate when the user releases the key
	switch (event.key) {
		case ' ':
			store.dispatch(activateLastTool());
			break;
		case 'z':
		case 'Z':
			// because z also triggers undo, make sure we don't accidentally pop a tool off the stack
			// when releasing an undo keystroke
			if (store.getState().mode.activeTool === ToolType.ZOOM) {
				store.dispatch(activateLastTool());
			}
			break;
		case 'Alt':
			store.dispatch(setToolModified(false));
			break;
	}
};

enum ElementFocusState {
	FIELD = 'field',
	UNDO_EXEMPT_FIELD = 'undo-exempt-field',
	NON_FIELD = 'non-field',
	OTHER = 'other',
}

const getElementFocusState = (): ElementFocusState => {
	const { activeElement } = document;

	if (activeElement) {
		// Check if activeElement is inside Shadow DOM
		// When an input is inside Shadow DOM, document.activeElement returns the shadow host,
		// but we need to check the actual focused element inside the shadow root
		let actualFocusedElement: Element | null = activeElement;

		// If activeElement has a shadowRoot and the shadowRoot has an activeElement,
		// that's the real focused element
		if (activeElement.shadowRoot?.activeElement) {
			actualFocusedElement = activeElement.shadowRoot.activeElement;
		}

		if (actualFocusedElement) {
			const tagName = actualFocusedElement.tagName.toLowerCase();
			if (['input', 'textarea'].includes(tagName)) {
				if (
					(actualFocusedElement as HTMLInputElement | HTMLTextAreaElement).dataset.undoExempt ===
					'true'
				) {
					return ElementFocusState.UNDO_EXEMPT_FIELD;
				}
				return ElementFocusState.FIELD;
			}
			if (['button', 'select'].includes(tagName)) {
				return ElementFocusState.NON_FIELD;
			}
		}
	}

	return ElementFocusState.OTHER;
};

// Check if any modal is visible
const isAnyModalVisible = (): boolean => {
	const state = store.getState().mode;
	return (
		state.promptModal !== null ||
		state.confirmModal !== null ||
		state.alertModal !== null ||
		state.settingsModalVisible ||
		state.visibleToolModal !== null
	);
};

// these utility functions describe the "open" canvas area, by that we mean
// the visible work area between the panels/toolbars/etc.
export const openCanvasWidth = (state: Store) => {
	const { canvasWidth, panelsVisible, leftPanelWidths, activeLeftPanelTab, rightPanelWidth } =
		state;
	if (canvasWidth === null) return 0;

	const leftWidth = panelsVisible ? leftPanelWidths[activeLeftPanelTab] : 0;
	const rightWidth = panelsVisible ? rightPanelWidth : 0;

	return canvasWidth - (leftWidth + rightWidth);
};
//
export const openCanvasLeft = (state: Store): number => {
	const { panelsVisible, leftPanelWidths, activeLeftPanelTab } = state;
	return panelsVisible ? leftPanelWidths[activeLeftPanelTab] : 0;
};
//
export const openCanvasHeight = (state: Store): number => {
	const { canvasHeight } = state;
	const toolIslandHeightAndPadding = 60;
	if (canvasHeight === null) return 0;
	return canvasHeight - toolIslandHeightAndPadding;
};

// computes a new offset so that, when we update the scale to the provided
// scale, the provided position (in canvas coordinates) remains
// at the same position on the screen
export const getOffsetToPreservePositionWhenScaling = (
	state: Store,
	canvasPosition: Point,
	scale: number
): Point => {
	// update offset so that the same scene point is beneath the mouse
	// before, and after we change the scale
	//
	// what is the scene position with the old scale?
	// remember our transformation setup is:
	//     canvas.x = (scale * scene.x) + offset.x
	//     canvas.y = (scale * scene.y) + offset.y
	const transform = selectTransform(state);

	const scenePositionBeforeScaling = {
		x: (canvasPosition.x - transform.offset.x) / transform.scale,
		y: (canvasPosition.y - transform.offset.y) / transform.scale,
	};

	// with the new scale, and the old offset, where does this scene point
	// end up on the canvas?
	// according to our rules, we can solve for scene point like so:
	//     scene.x = (canvas.x - offset.x) / scale
	//     scene.y = (canvas.y - offset.y) / scale
	const canvasPositionAfterScaling = {
		x: scale * scenePositionBeforeScaling.x + transform.offset.x,
		y: scale * scenePositionBeforeScaling.y + transform.offset.y,
	};

	// change the offset so that the same scene point is beneath the mouse
	const delta = {
		x: canvasPosition.x - canvasPositionAfterScaling.x,
		y: canvasPosition.y - canvasPositionAfterScaling.y,
	};

	return {
		x: transform.offset.x + delta.x,
		y: transform.offset.y + delta.y,
	} as Point;
};

export const shiftXOffset = (state: Store, deltaX: number): Point => {
	const transform = selectTransform(state);
	return {
		x: transform.offset.x + deltaX,
		y: transform.offset.y,
	};
};

export const shiftYOffset = (state: Store, deltaY: number): Point => {
	const transform = selectTransform(state);
	return {
		x: transform.offset.x,
		y: transform.offset.y + deltaY,
	};
};

export const canvasOffsetX = () => {
	const { panelsVisible, leftPanelWidths, activeLeftPanelTab } = store.getState().mode;
	return panelsVisible ? leftPanelWidths[activeLeftPanelTab] : 0;
};

// if the frame with frameId is picked, return true
// if something other than a frame (such as some elements) are selected, return false
export const framePicked = (picks: Pick[], frameId?: string): boolean => {
	if (!frameId) return false;
	return picks.find((s) => s.frameId && s.frameId === frameId && s.isFrame()) ? true : false;
};

// if only one frame selected, return its frameId
export const getSelectedFrameId = (picks: Pick[]): string | null => {
	const selected = picks.filter((s) => s.frameId);
	if (selected.length === 1) return selected[0].frameId || null;
	return null;
};

export const getSelectedFrameIds = (picks: Pick[]): string[] => {
	const frameIds = picks
		.filter((pick) => pick.isFrame() && pick.frameId)
		.map((pick) => pick.frameId) as string[];
	return frameIds;
};

export const getFrameIds = (picks: Pick[]): string[] => {
	const frameIds = picks
		.filter((pick) => pick.isFrame() && pick.frameId)
		.map((pick) => pick.frameId) as string[];
	return frameIds;
};

export const getLeftPanelWidth = (state: Store) => {
	const { panelsVisible, leftPanelWidths, activeLeftPanelTab } = state;
	return panelsVisible ? leftPanelWidths[activeLeftPanelTab] : 0;
};
